import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { format } from 'date-fns';
import { History, Droplets, Bug, Snowflake, ArrowUpCircle, DoorOpen, FileText, ChevronDown, ChevronUp, Home } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_ICONS = {
  feeding: Droplets,
  treatment: Bug,
  winterization: Snowflake,
  super_added: ArrowUpCircle,
  super_removed: ArrowUpCircle,
  bee_escape: DoorOpen,
  note: FileText
};

const ACTION_COLORS = {
  feeding: 'bg-blue-100 text-blue-700 border-blue-200',
  treatment: 'bg-red-100 text-red-700 border-red-200',
  winterization: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  super_added: 'bg-green-100 text-green-700 border-green-200',
  super_removed: 'bg-orange-100 text-orange-700 border-orange-200',
  bee_escape: 'bg-purple-100 text-purple-700 border-purple-200',
  note: 'bg-stone-100 text-stone-700 border-stone-200'
};

const ACTION_LABELS = {
  feeding: 'Feeding',
  treatment: 'Treatment',
  winterization: 'Winterization',
  super_added: 'Super Added',
  super_removed: 'Super Removed',
  bee_escape: 'Bee Escape',
  note: 'Note Added'
};

const YardHistoryDialog = ({ open, onOpenChange, yard }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    if (open && yard) {
      fetchHistory();
    }
  }, [open, yard]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/yard-actions/${encodeURIComponent(yard.name)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data || []);
    } catch (error) {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const formatActionDetails = (action) => {
    const details = [];
    
    if (action.action_data?.feeding_details) {
      const fd = action.action_data.feeding_details;
      details.push(`Type: ${fd.feed_type}`);
      if (fd.quantity) details.push(`Quantity: ${fd.quantity} ${fd.unit || ''}`);
      if (fd.method) details.push(`Method: ${fd.method}`);
    }
    
    if (action.action_data?.treatment_details) {
      const td = action.action_data.treatment_details;
      details.push(`Treatment: ${td.treatment_type}`);
      if (td.treatment_for) details.push(`For: ${td.treatment_for}`);
      if (td.dosage) details.push(`Dosage: ${td.dosage} ${td.dosage_unit || ''}`);
    }
    
    if (action.action_data?.winterization_details) {
      const wd = action.action_data.winterization_details;
      const applied = [];
      if (wd.insulation_added) applied.push('Insulation');
      if (wd.entrance_reducer) applied.push('Entrance Reducer');
      if (wd.mouse_guard) applied.push('Mouse Guard');
      if (wd.moisture_board) applied.push('Moisture Board');
      if (wd.candy_board) applied.push('Candy Board');
      if (applied.length) details.push(`Applied: ${applied.join(', ')}`);
    }
    
    if (action.action_data?.super_details) {
      const sd = action.action_data.super_details;
      details.push(`${sd.action === 'add' ? 'Added' : 'Removed'} ${sd.quantity} ${sd.super_type} super(s)`);
      if (sd.frame_size) details.push(`Frame size: ${sd.frame_size}`);
    }
    
    if (action.action_data?.notes) {
      details.push(`Notes: ${action.action_data.notes}`);
    }
    
    return details;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-sky-500" />
            {yard?.name} - Action History
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <History className="h-12 w-12 mx-auto mb-3 text-stone-300" />
              <p>No bulk actions recorded for this yard yet.</p>
              <p className="text-sm mt-1">Use "Apply to All" when viewing hives in this yard to record bulk actions.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((action) => {
                const Icon = ACTION_ICONS[action.action_type] || FileText;
                const colorClass = ACTION_COLORS[action.action_type] || ACTION_COLORS.note;
                const isExpanded = expandedItems[action.id];
                const details = formatActionDetails(action);
                
                return (
                  <div 
                    key={action.id} 
                    className={`border rounded-lg overflow-hidden ${colorClass}`}
                  >
                    <button
                      onClick={() => toggleExpand(action.id)}
                      className="w-full p-4 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        <div>
                          <div className="font-semibold">
                            {ACTION_LABELS[action.action_type] || action.action_type}
                          </div>
                          <div className="text-xs opacity-75">
                            {action.date ? format(new Date(action.date), 'PPp') : 'Date unknown'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-sm">
                          <Home className="h-3.5 w-3.5" />
                          {action.hive_count} hive{action.hive_count !== 1 ? 's' : ''}
                        </div>
                        {details.length > 0 && (
                          isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </button>
                    
                    {isExpanded && details.length > 0 && (
                      <div className="px-4 pb-4 pt-0 border-t border-current/10">
                        <ul className="text-sm space-y-1 mt-3">
                          {details.map((detail, idx) => (
                            <li key={idx} className="opacity-90">{detail}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default YardHistoryDialog;
